import type { ReactNode } from 'react';
import { Alert, Typography } from 'antd';
import { Link } from 'react-router';
import { useCalibration } from '../app/calibration-context';
import { cmToPx } from '../lib/calibration';

export type PreviewSize = 'original' | 3 | 5;

/**
 * 이미지를 지정한 물리 크기(정사각 cm)로 표시한다. 'original'이면 원본 픽셀 크기(화면 폭을 넘으면 축소).
 * 카드 경계를 알 수 있도록 테두리를 그리고, 이미지는 비율을 유지해 상자 안에 맞춘다.
 */
export function PhysicalImage({ src, alt, size, caption }: { src: string; alt: string; size: PreviewSize; caption?: ReactNode }) {
  const { status } = useCalibration();
  if (size === 'original') {
    return <img src={src} alt={alt} style={{ maxWidth: '100%', height: 'auto', display: 'block', background: CHECKER, imageRendering: 'auto' }} />;
  }
  const px = cmToPx(size, status.pxPerCm);
  const box = (
    <div
      data-testid="physical-box"
      data-size-cm={size}
      style={{ width: px, height: px, flex: 'none', outline: '1px solid #d9d9d9', background: CHECKER, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
    </div>
  );
  if (caption == null) return box;
  return (
    <div style={{ width: px, flex: 'none' }}>
      {box}
      <Typography.Text style={{ fontSize: 12, display: 'block', marginTop: 4 }} ellipsis>{caption}</Typography.Text>
    </div>
  );
}

// 투명 영역이 드러나도록 옅은 체크무늬 배경
const CHECKER = 'repeating-conic-gradient(#f3f3f3 0% 25%, #ffffff 0% 50%) 50% / 16px 16px';

/** 보정 상태 안내. 보정 전에는 근사치임을 표시한다(R1 §11.3). */
export function CalibrationNotice({ showLink = true }: { showLink?: boolean }) {
  const { status } = useCalibration();
  if (status.state === 'calibrated') {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        화면 보정 적용됨 ({new Date(status.savedAt).toLocaleDateString('ko-KR')}{status.rescaled ? ', 확대 비율 변경을 환산함' : ''})
        {showLink && <> · <Link to="/preview">다시 보정</Link></>}
      </Typography.Text>
    );
  }
  const why = status.reason === 'other-screen' ? '이 화면은 아직 보정하지 않았습니다(다른 화면의 보정값만 있음).' : '화면 보정 전입니다.';
  return (
    <Alert
      type="warning"
      showIcon
      message={`${why} 표시 크기는 실제 크기의 근사치입니다.`}
      description={showLink ? <Link to="/preview">실물 크기 검수에서 화면 보정하기</Link> : undefined}
    />
  );
}
